// Real estate-focused service landing page data.
// Used by /services/:slug dynamic route and homepage service cards.

export interface ServiceFAQ {
  q: string;
  a: string;
}

export interface ServiceDefinition {
  slug: string;
  title: string;
  h1: string;
  shortLabel: string;
  metaTitle: string;
  metaDescription: string;
  heroEyebrow: string;
  heroSubhead: string;
  problem: {
    headline: string;
    points: string[];
  };
  solution: {
    headline: string;
    body: string;
  };
  whatsIncluded: string[];
  process: { step: string; title: string; body: string }[];
  kpis: { label: string; value: string }[];
  faqs: ServiceFAQ[];
  related: string[]; // slugs
  keywords: string[];
}

const commonProcess = [
  { step: "01", title: "Discovery Call", body: "30-minute call to map your market, offer, ideal seller profile, and pipeline gaps." },
  { step: "02", title: "Recruit & Train", body: "We hand-pick US-fluent acquisition talent and train them on your scripts, objections, and CRM." },
  { step: "03", title: "Launch in 10, 14 Days", body: "Dialers, lists, KPIs, and reporting wired up. First dials go out within two weeks." },
  { step: "04", title: "Optimize Weekly", body: "Weekly KPI reviews on dials, contacts, leads, and appointments. We coach the team to your numbers." },
];

export const services: ServiceDefinition[] = [
  {
    slug: "real-estate-cold-calling",
    title: "Real Estate Cold Calling Services",
    h1: "Real Estate Cold Calling That Books Off-Market Deals",
    shortLabel: "Cold Calling",
    metaTitle: "Real Estate Cold Calling Services for US Investors | Elevate Global",
    metaDescription: "Trained real estate cold callers for US investors, wholesalers, and cash buyers. 200+ dials/day per caller, scripted for off-market seller leads.",
    heroEyebrow: "For US Real Estate Investors & Wholesalers",
    heroSubhead: "Dedicated cold callers trained on your market, your script, and your CRM, booking motivated-seller appointments at a fraction of US hiring cost.",
    problem: {
      headline: "Why most investors stall on cold calling",
      points: [
        "US cold callers cost $4,500, $7,000/mo loaded, and most quit in 90 days.",
        "Local VAs read the script flat and burn through expensive seller lists.",
        "Without consistent dial volume, your pipeline dies between deals.",
      ],
    },
    solution: {
      headline: "A trained acquisition caller, working US hours, at offshore cost",
      body: "We recruit, vet, and train English-fluent cold callers specifically for US real estate, distressed, absentee, probate, pre-foreclosure, and tax delinquent lists. They work your time zone, use your dialer, and feed leads straight into your CRM.",
    },
    whatsIncluded: [
      "Dedicated full-time cold caller (no shared agents)",
      "Script writing and objection handling for your market",
      "Dialer setup (Mojo, Batch, Smarter, PhoneBurner, etc.)",
      "Daily dial logs and weekly KPI reports",
      "Live coaching and call reviews",
      "Replacement guarantee if a caller underperforms",
    ],
    process: commonProcess,
    kpis: [
      { label: "Dials per caller/day", value: "180, 250" },
      { label: "Contacts per day", value: "25, 40" },
      { label: "Leads per week", value: "8, 15" },
      { label: "Launch time", value: "10, 14 days" },
    ],
    faqs: [
      { q: "Do you provide the seller list or do I?", a: "Most investors bring their own pulled lists (PropStream, BatchLeads, ListSource). We can also recommend list vendors and clean lists before dialing." },
      { q: "What dialer do you use?", a: "We work with whatever you already use, Mojo, Batch Dialer, Smarter Contact, PhoneBurner, ReadyMode, CallTools, or your CRM's built-in dialer." },
      { q: "How many dials per day per caller?", a: "180, 250 connected dials per caller per day depending on list quality and answer rate." },
      { q: "How is a 'lead' defined?", a: "A motivated seller who is open to an offer, with property details, timeline, and contact info captured in your CRM." },
      { q: "Do callers work US time zones?", a: "Yes, EST, CST, MST, or PST. Most dial 9am, 6pm in the assigned time zone." },
      { q: "What if a caller isn't performing?", a: "We coach first; if numbers don't move within 2 weeks we replace at no cost." },
      { q: "Can you also set the appointment with me?", a: "Yes, cold caller hands off warm leads to an appointment setter who books directly on your calendar." },
    ],
    related: ["appointment-setting", "acquisition-managers", "investor-follow-up"],
    keywords: ["real estate cold calling", "cold callers for real estate investors", "real estate ISA services", "off market lead generation"],
  },
  {
    slug: "acquisition-managers",
    title: "Acquisition Manager Services",
    h1: "Trained Acquisition Managers for US Real Estate Investors",
    shortLabel: "Acquisition Managers",
    metaTitle: "Acquisition Manager Services for Real Estate Investors | Elevate Global",
    metaDescription: "Hire a trained offshore acquisition manager to qualify seller leads, run comps, present offers, and lock up contracts for your US real estate business.",
    heroEyebrow: "Offshore Acquisition Talent for US Investors",
    heroSubhead: "Full-time acquisition managers who qualify leads, run comps, negotiate with sellers, and lock up contracts, so you stop being the bottleneck.",
    problem: {
      headline: "You can't scale if you're the only one closing",
      points: [
        "You're the highest-paid hour in your business, and you're still on every seller call.",
        "Deals slip because no one is following up the day a lead comes in.",
        "Hiring a US acquisition manager runs $80K, $120K base before commission.",
      ],
    },
    solution: {
      headline: "An acquisition manager who runs your pipeline end-to-end",
      body: "We place a dedicated acquisition manager trained on wholesaling, novations, sub-to, creative finance, and standard cash offers. They qualify the lead, build the offer, present it, and lock up the contract, under your direction.",
    },
    whatsIncluded: [
      "Dedicated full-time acquisition manager",
      "Comps and offer-prep training for your buy box",
      "Daily lead-status pipeline review",
      "Contract prep and seller negotiation",
      "Weekly KPI scorecard (leads → offers → contracts)",
      "Direct Slack/Teams communication",
    ],
    process: commonProcess,
    kpis: [
      { label: "Leads worked/week", value: "40, 80" },
      { label: "Offers sent/week", value: "10, 20" },
      { label: "Contracts/month (target)", value: "2, 6" },
      { label: "Cost vs US hire", value: "~70% less" },
    ],
    faqs: [
      { q: "Can they present offers to sellers?", a: "Yes, that's the core of the role. They run comps, build the offer, present it, handle objections, and lock up." },
      { q: "Do they handle creative finance?", a: "We train on sub-to, novations, seller financing, and lease options on request." },
      { q: "Do they sign contracts?", a: "They prep and walk sellers through the agreement; final e-signature flow stays under your control." },
      { q: "What CRM do they work in?", a: "GoHighLevel, Podio, REI Reply, FollowUpBoss, Salesforce, HubSpot, any major REI CRM." },
      { q: "How fast can one start?", a: "10, 14 days from kickoff." },
      { q: "Can I split one across cold-calling and acquisitions?", a: "We don't recommend it, focused roles convert higher. We can pair an acquisition manager with a dedicated cold caller instead." },
    ],
    related: ["real-estate-cold-calling", "investor-pipeline-management", "investor-follow-up"],
    keywords: ["acquisition manager services", "offshore acquisition managers", "real estate acquisitions"],
  },
  {
    slug: "appointment-setting",
    title: "Appointment Setting for Real Estate Investors",
    h1: "Real Estate Appointment Setting, Calendar Filled With Sellers",
    shortLabel: "Appointment Setting",
    metaTitle: "Real Estate Appointment Setting for Investors | Elevate Global",
    metaDescription: "Trained appointment setters book qualified seller and cash-buyer calls directly on your calendar. Built for US real estate investors and wholesalers.",
    heroEyebrow: "Calendar Coverage for Acquisition Teams",
    heroSubhead: "Dedicated setters who qualify the lead, confirm motivation and timeline, and drop a booked appointment straight on your calendar.",
    problem: {
      headline: "Leads die in the gap between dial and demo",
      points: [
        "Most leads ghost within 5 minutes of going cold, speed-to-lead is everything.",
        "Closers spend half their day chasing setters instead of negotiating.",
        "No-show rates kill morale and waste pipeline.",
      ],
    },
    solution: {
      headline: "Booked, confirmed appointments, your closers just show up",
      body: "Setters work warm and cold inbound leads, qualify against your buy box, and book the call. They send reminders, reschedule no-shows, and keep your acquisition manager's calendar full.",
    },
    whatsIncluded: [
      "Dedicated full-time appointment setter",
      "Inbound + outbound lead handling",
      "Calendar integration (Calendly, GHL, HubSpot)",
      "SMS + email confirmation sequences",
      "No-show recovery workflow",
      "Daily booking report",
    ],
    process: commonProcess,
    kpis: [
      { label: "Booked/week (avg)", value: "10, 25" },
      { label: "Show rate", value: "60, 75%" },
      { label: "Speed to lead", value: "<5 min" },
      { label: "Launch time", value: "10 days" },
    ],
    faqs: [
      { q: "Do setters cold call or only handle warm leads?", a: "Both, we deploy based on whether you have a cold-calling team feeding them or need them to source." },
      { q: "How do they know if a lead is qualified?", a: "We build your buy-box checklist into the call script: motivation, timeline, condition, mortgage status, asking price." },
      { q: "Can they book on multiple closers' calendars?", a: "Yes, round-robin or assigned to a specific acquisition manager." },
      { q: "Do they handle SMS too?", a: "Yes, most setters work voice + SMS via your CRM." },
      { q: "How do you handle no-shows?", a: "Automated reminder sequence + manual reschedule attempt within an hour." },
      { q: "Can they work nights/weekends?", a: "Yes, common for cash-buyer outreach. Add hours as needed." },
    ],
    related: ["real-estate-cold-calling", "investor-follow-up", "acquisition-managers"],
    keywords: ["real estate appointment setting", "appointment setters for investors"],
  },
  {
    slug: "real-estate-virtual-assistants",
    title: "Real Estate Virtual Assistants",
    h1: "Real Estate Virtual Assistants Built for Investors",
    shortLabel: "Real Estate VAs",
    metaTitle: "Real Estate Virtual Assistants for US Investors | Elevate Global",
    metaDescription: "Hire a trained real estate virtual assistant for list pulling, skip tracing, CRM management, dispositions, transaction coordination, and admin.",
    heroEyebrow: "Operational Leverage for REI Teams",
    heroSubhead: "Full-time real estate VAs for list pulling, skip tracing, CRM hygiene, dispositions support, and transaction coordination, so your closers can close.",
    problem: {
      headline: "Your team is doing $15/hr work on $200/hr time",
      points: [
        "Closers waste hours on data entry, list pulling, and CRM cleanup.",
        "Transactions slip because no one is chasing title, inspection, and EMD.",
        "Dispositions sit idle without consistent buyer outreach.",
      ],
    },
    solution: {
      headline: "A trained REI VA, embedded in your workflow",
      body: "Full-time, US-hours-aligned VAs trained for real estate: PropStream/BatchLeads list pulling, skip tracing, CRM updates, buyer-list outreach, document prep, and transaction coordination.",
    },
    whatsIncluded: [
      "Dedicated full-time VA (40 hrs/week)",
      "List pulling + skip tracing",
      "CRM hygiene and lead tagging",
      "Dispositions outreach to buyer list",
      "Transaction coordination support",
      "Email + calendar management",
    ],
    process: commonProcess,
    kpis: [
      { label: "Hrs/week reclaimed", value: "30, 40" },
      { label: "Cost vs US VA", value: "~60% less" },
      { label: "Tools trained on", value: "20+" },
      { label: "Launch time", value: "7, 10 days" },
    ],
    faqs: [
      { q: "What tools are your VAs trained on?", a: "PropStream, BatchLeads, BatchSkipTracing, REISkip, GoHighLevel, Podio, FollowUpBoss, DocuSign, Dotloop, Zillow, Redfin, ListSource, and most major REI stacks." },
      { q: "Can a VA also cold call?", a: "We recommend separate roles, focused VAs outperform hybrid setups. Pair a VA with a dedicated cold caller." },
      { q: "Do they handle transaction coordination?", a: "Yes, title coordination, EMD tracking, inspection scheduling, and closing checklist." },
      { q: "How do I communicate daily?", a: "Slack, Teams, or WhatsApp, your choice. Daily standup + EOD report." },
      { q: "Can I scale to multiple VAs?", a: "Yes, most teams start with one and add a second within 60, 90 days." },
      { q: "Do you provide equipment?", a: "Yes, VAs come with their own laptop, headset, and high-speed backup internet." },
    ],
    related: ["crm-management", "investor-follow-up", "acquisition-managers"],
    keywords: ["real estate virtual assistant", "virtual assistants for investors", "REI VA"],
  },
  {
    slug: "cash-buyer-lead-generation",
    title: "Lead Generation for Cash Buyers",
    h1: "Cash-Buyer Lead Generation for Wholesalers & Investors",
    shortLabel: "Cash Buyer Lead Gen",
    metaTitle: "Cash Buyer Lead Generation for Real Estate Wholesalers | Elevate Global",
    metaDescription: "Build a vetted cash-buyer list for your market. Outbound outreach, list building, and dispo support that helps wholesalers move contracts faster.",
    heroEyebrow: "Move Contracts In Days, Not Weeks",
    heroSubhead: "We build, qualify, and warm-up a vetted cash-buyer list in your market, and run dispo outreach the moment a contract gets locked.",
    problem: {
      headline: "A weak buyer list kills your assignment fee",
      points: [
        "Contracts age out because there's no warm buyer ready to take it.",
        "JV partners cut your fee in half because they bring the buyer.",
        "You're guessing on price without real comps from active buyers.",
      ],
    },
    solution: {
      headline: "A live, qualified buyer list, refreshed monthly",
      body: "We pull and qualify cash buyers from public records, FB groups, REI meetups, and county data, verify buy box, price range, financing, and area, and stay in touch via SMS + email.",
    },
    whatsIncluded: [
      "200, 500 qualified cash buyers/month",
      "Buy-box capture (price, ZIP, condition, financing)",
      "Monthly list refresh and re-qualification",
      "Dispo blast SMS + email on locked contracts",
      "JV partner outreach",
      "Buyer CRM tagging",
    ],
    process: commonProcess,
    kpis: [
      { label: "New buyers/month", value: "200, 500" },
      { label: "Avg dispo time", value: "3, 10 days" },
      { label: "Buyer touches/month", value: "2+" },
      { label: "Markets covered", value: "Any US ZIP" },
    ],
    faqs: [
      { q: "Where do the buyers come from?", a: "Cash sales records, county data, PropStream, MLS, FB groups, REI meetup attendee lists, and inbound from your dispo blasts." },
      { q: "Are buyers verified?", a: "Yes, proof of funds question, buy-box capture, and area confirmation before they're added to the active list." },
      { q: "Do you also send the dispo blast?", a: "Yes, SMS + email within an hour of a contract being locked." },
      { q: "How big should my buyer list be?", a: "Aim for 500+ active buyers per market for predictable dispo." },
      { q: "Do you guarantee the contract moves?", a: "No one ethical guarantees an assignment, but a warm 500+ buyer list cuts average dispo time dramatically." },
      { q: "Can you also build an investor capital list?", a: "Yes, same workflow for private money lenders." },
    ],
    related: ["wholesaling-support", "investor-pipeline-management", "real-estate-cold-calling"],
    keywords: ["cash buyer lead generation", "lead generation for wholesalers", "cash buyer list"],
  },
  {
    slug: "investor-follow-up",
    title: "Follow-Up Services for Real Estate Investors",
    h1: "Follow-Up Systems That Resurrect Dead Real Estate Leads",
    shortLabel: "Follow-Up Systems",
    metaTitle: "Real Estate Follow-Up Services for Investors | Elevate Global",
    metaDescription: "Long-term follow-up systems and dedicated callers that work your old seller leads, nurture them for months, and pull live deals out of dead lists.",
    heroEyebrow: "60% of Deals Come After the 5th Touch",
    heroSubhead: "Dedicated long-term follow-up, calls, SMS, email, voicemail drops, running for 6, 12, 24+ months on every lead until they sell.",
    problem: {
      headline: "Your dead-lead pile is your biggest unworked asset",
      points: [
        "Most teams quit after 2 touches; the average deal closes after 5, 12.",
        "Manual follow-up never happens consistently, life gets in the way.",
        "Sellers' timelines change; the 'no' today is a 'yes' in 6 months.",
      ],
    },
    solution: {
      headline: "Systematic, multi-channel follow-up at scale",
      body: "We set up the cadence (call → SMS → voicemail drop → email → repeat at 7/14/30/60/90 days), assign a dedicated caller to work the queue, and track every touch in your CRM until the seller is dead, sold, or signs.",
    },
    whatsIncluded: [
      "Multi-channel cadence build (call/SMS/email/RVM)",
      "Dedicated follow-up caller",
      "Old-lead resurrection campaign",
      "Drip sequences in your CRM",
      "Weekly resurrected-lead report",
      "A/B-tested touch templates",
    ],
    process: commonProcess,
    kpis: [
      { label: "Cadence touches/lead", value: "12, 24" },
      { label: "Resurrection rate", value: "3, 8%" },
      { label: "Avg touches to close", value: "5, 12" },
      { label: "Lead horizon", value: "24+ months" },
    ],
    faqs: [
      { q: "How long is the follow-up cadence?", a: "Most investors run a 12, 24 month cadence with declining frequency. We customize per lead temperature." },
      { q: "Will you work my old lead list?", a: "Yes, old-lead resurrection is one of our highest-ROI campaigns. Most lists have 3, 8% deals hiding inside." },
      { q: "What CRM do you set this up in?", a: "GoHighLevel, FollowUpBoss, Podio, REI Reply, HubSpot, or whatever you use." },
      { q: "Do you write the SMS/email templates?", a: "Yes, included." },
      { q: "Is this compliant with TCPA?", a: "We follow opt-out rules and STOP keywords; we recommend you have signed consent or warm lead status for SMS." },
      { q: "Can the same caller also book the appointment?", a: "Yes, when a lead warms up, our setter books the call." },
    ],
    related: ["real-estate-cold-calling", "crm-management", "appointment-setting"],
    keywords: ["real estate follow up systems", "follow up for real estate investors", "old lead resurrection"],
  },
  {
    slug: "crm-management",
    title: "CRM Management for Real Estate Investors",
    h1: "CRM Management for Real Estate Investors",
    shortLabel: "CRM Management",
    metaTitle: "Real Estate CRM Management Services | GoHighLevel, Podio, FUB | Elevate Global",
    metaDescription: "Build, manage, and automate your real estate CRM. GoHighLevel, Podio, REI Reply, FollowUpBoss, pipelines, automations, reporting, and clean data.",
    heroEyebrow: "Stop Losing Deals to Bad CRM Hygiene",
    heroSubhead: "We build and manage your REI CRM, pipelines, automations, SMS/email sequences, reporting dashboards, so every lead gets worked the same way every time.",
    problem: {
      headline: "Your CRM is a graveyard, not a sales engine",
      points: [
        "Pipeline stages don't reflect how your team actually works.",
        "Automations send half the team wrong messages, or none at all.",
        "Nobody knows what's actually in the pipeline at any given moment.",
      ],
    },
    solution: {
      headline: "A working CRM that drives daily action",
      body: "We architect pipelines around your acquisition process, build SMS + email automations, set up KPI dashboards, and clean up duplicate/dead records so leadership has real visibility.",
    },
    whatsIncluded: [
      "Pipeline + stage architecture",
      "SMS, email, and voicemail-drop automations",
      "Lead-source tracking + attribution",
      "KPI dashboard (leads → offers → contracts)",
      "Duplicate cleanup + data hygiene",
      "Team training + SOPs",
    ],
    process: commonProcess,
    kpis: [
      { label: "CRMs supported", value: "GHL, Podio, FUB, HubSpot" },
      { label: "Setup time", value: "2, 3 weeks" },
      { label: "Avg automations built", value: "15, 40" },
      { label: "Data cleanup", value: "100%" },
    ],
    faqs: [
      { q: "Which CRM do you recommend for REI?", a: "GoHighLevel for most wholesalers and small teams; Podio or FollowUpBoss for higher-volume operations." },
      { q: "Can you migrate from one CRM to another?", a: "Yes, we handle full data migration, pipeline rebuild, and team training." },
      { q: "Do you build the automations or just configure?", a: "Both, workflows, triggers, conditional logic, and AI-routing." },
      { q: "Will you train my team?", a: "Yes, recorded SOPs + live training session included." },
      { q: "Do you offer ongoing management after setup?", a: "Yes, monthly admin/optimization retainers available." },
      { q: "Can you integrate dialers?", a: "Mojo, Smarter Contact, Batch Dialer, PhoneBurner, ReadyMode, CallTools, all supported." },
    ],
    related: ["investor-pipeline-management", "investor-follow-up", "real-estate-virtual-assistants"],
    keywords: ["CRM management for real estate", "GoHighLevel REI", "real estate CRM setup"],
  },
  {
    slug: "investor-pipeline-management",
    title: "Investor Pipeline Management",
    h1: "Investor Pipeline Management, Run Your Operation Like a Sales Org",
    shortLabel: "Pipeline Management",
    metaTitle: "Real Estate Investor Pipeline Management | Elevate Global",
    metaDescription: "Full pipeline management for real estate investors, daily lead triage, KPI tracking, team accountability, weekly reporting, and pipeline forecasting.",
    heroEyebrow: "Operational Layer for Scaling REI Teams",
    heroSubhead: "We run the daily pipeline so you don't have to, lead triage, KPI tracking, team accountability, and weekly forecasting for your acquisition operation.",
    problem: {
      headline: "Scaling REI fails at the operations layer",
      points: [
        "You have callers, setters, and acquisitions, but no one running the floor.",
        "Numbers slip and you find out 3 weeks too late.",
        "Forecasting next month's deal flow is a guess.",
      ],
    },
    solution: {
      headline: "A dedicated pipeline manager keeping every role on number",
      body: "Our pipeline manager runs daily standups, triages new leads to the right team member, audits CRM hygiene, and produces weekly leadership reports with real KPIs and forecasts.",
    },
    whatsIncluded: [
      "Daily lead triage + assignment",
      "KPI scorecards by role",
      "Weekly leadership report",
      "30/60/90 day pipeline forecast",
      "Team accountability standups",
      "CRM audit + cleanup",
    ],
    process: commonProcess,
    kpis: [
      { label: "Reports", value: "Weekly + monthly" },
      { label: "Forecast horizon", value: "90 days" },
      { label: "Roles managed", value: "Callers, setters, acq" },
      { label: "Launch time", value: "10 days" },
    ],
    faqs: [
      { q: "How is this different from an acquisition manager?", a: "Acquisition managers close deals; pipeline managers run the operation around them, triage, KPIs, reporting." },
      { q: "Do you need access to my CRM?", a: "Yes, admin-level access to pull reports and update lead assignments." },
      { q: "What reports do I get?", a: "Daily activity, weekly KPI scorecard, monthly P&L per lead source, 90-day pipeline forecast." },
      { q: "Can you manage my US team too?", a: "Yes, we can run the floor for hybrid US + offshore teams." },
      { q: "Do you replace my COO?", a: "No, this is a tactical pipeline-ops layer, not strategic leadership." },
      { q: "What KPIs do you track?", a: "Dials, contacts, leads, appointments, offers sent, contracts locked, dispo time, close rate, revenue per lead source." },
    ],
    related: ["acquisition-managers", "crm-management", "investor-follow-up"],
    keywords: ["investor pipeline management", "real estate sales operations"],
  },
  {
    slug: "wholesaling-support",
    title: "Wholesaling Support Services",
    h1: "End-to-End Wholesaling Support for US Real Estate",
    shortLabel: "Wholesaling Support",
    metaTitle: "Real Estate Wholesaling Support Services | Elevate Global",
    metaDescription: "Cold calling, acquisitions, dispositions, and transaction coordination for US wholesalers. A full offshore wholesaling team built around your buy box.",
    heroEyebrow: "A Full Offshore Wholesaling Team",
    heroSubhead: "Cold calling, acquisitions, dispositions, transaction coordination, every seat in a wholesale operation, built around your buy box and market.",
    problem: {
      headline: "Wholesaling is 5 jobs disguised as 1",
      points: [
        "Solo wholesalers can't dial, qualify, comp, negotiate, and dispo all day.",
        "Hiring 5 US roles costs $300K/year before commission.",
        "You're leaving deals on the table because something always slips.",
      ],
    },
    solution: {
      headline: "Drop a full wholesaling team into your business in 14 days",
      body: "We assemble a turnkey offshore team, cold caller, acquisition manager, dispo VA, transaction coordinator, trained on wholesaling fundamentals, your buy box, and your CRM.",
    },
    whatsIncluded: [
      "Dedicated cold caller(s)",
      "Acquisition manager",
      "Dispositions VA",
      "Transaction coordinator",
      "Pipeline manager (optional)",
      "Weekly KPI review + coaching",
    ],
    process: commonProcess,
    kpis: [
      { label: "Roles deployed", value: "3, 5" },
      { label: "Avg launch", value: "14 days" },
      { label: "Cost vs US team", value: "~70% less" },
      { label: "Target contracts/mo", value: "3, 8" },
    ],
    faqs: [
      { q: "I'm a solo wholesaler, is this overkill?", a: "Start with cold caller + acquisition manager. Add dispo and TC as deal flow grows." },
      { q: "Do you teach wholesaling or expect me to know it?", a: "We assume you run the strategy; we execute it. We're not a wholesaling education company." },
      { q: "Can you handle creative finance too?", a: "Yes, sub-to, novations, seller financing, lease options." },
      { q: "What markets do you work?", a: "All 50 US states. We don't care about your market, we follow your buy box." },
      { q: "Do you handle dispositions?", a: "Yes, buyer list outreach, JV partners, offer presentation." },
      { q: "How long is the contract?", a: "Month-to-month. No long-term lock-in." },
    ],
    related: ["real-estate-cold-calling", "acquisition-managers", "cash-buyer-lead-generation"],
    keywords: ["wholesaling support services", "wholesaling team outsourcing"],
  },
  {
    slug: "offshore-acquisition-teams",
    title: "Offshore Acquisition Teams",
    h1: "Build an Offshore Acquisition Team for Your Real Estate Business",
    shortLabel: "Offshore Acquisition Teams",
    metaTitle: "Offshore Acquisition Teams for US Real Estate Investors | Elevate Global",
    metaDescription: "Build a full offshore acquisition team, callers, setters, acquisitions, VAs, trained for US real estate at a fraction of domestic hiring cost.",
    heroEyebrow: "Scale Without US Hiring",
    heroSubhead: "Build a full offshore acquisition team, cold callers, setters, acquisition managers, VAs, recruited, trained, and managed for your US real estate operation.",
    problem: {
      headline: "US hiring is the bottleneck on every scale plan",
      points: [
        "Loaded US acquisition cost: $300K+/year for caller + setter + acq.",
        "Hiring cycles take 60, 90 days; turnover resets you to zero.",
        "You can't grow if 80% of revenue gets eaten by payroll.",
      ],
    },
    solution: {
      headline: "A fully managed offshore acquisition team in 2 weeks",
      body: "We recruit from a vetted pool of US-fluent talent, train them on real estate fundamentals + your buy box, deploy them in your CRM/dialer, and manage performance with weekly KPIs and replacement guarantees.",
    },
    whatsIncluded: [
      "Custom team build (caller, setter, acq, VA, TC)",
      "Recruiting + vetting",
      "Real estate + script training",
      "Tools/dialer/CRM setup",
      "Weekly performance reviews",
      "Replacement guarantee",
    ],
    process: commonProcess,
    kpis: [
      { label: "Team size range", value: "2, 20+" },
      { label: "Cost vs US", value: "~70% less" },
      { label: "Launch", value: "14 days" },
      { label: "Retention (12mo)", value: ">85%" },
    ],
    faqs: [
      { q: "What's the typical team makeup?", a: "Most start with 1 caller + 1 acquisition manager and add a setter, VA, and TC as volume grows." },
      { q: "Are these contractors or full-time?", a: "Full-time, dedicated, working only on your account during US hours." },
      { q: "How is quality controlled?", a: "Weekly KPI review, call audits, and a no-cost replacement clause if performance lags." },
      { q: "Can I interview candidates?", a: "Yes, final selection is yours." },
      { q: "How fast can I scale up?", a: "Add a new seat in 7, 14 days." },
      { q: "What countries are your talent from?", a: "Primarily Egypt, strong US-aligned English, US time zone overlap, and a deep pool of sales-trained talent." },
    ],
    related: ["acquisition-managers", "wholesaling-support", "real-estate-cold-calling"],
    keywords: ["offshore acquisition teams", "real estate outsourcing", "offshore real estate team"],
  },
];

export const servicesBySlug = Object.fromEntries(services.map((s) => [s.slug, s]));
